import { useCallback, useEffect, useRef, useState } from 'react'
import { FadeInBox } from '@components/Transitions'
import { Box, BoxProps, IconButton, MotionBox } from '@ui'
import { SECOND_MS } from 'data/constants'
import { AnimatePresence } from 'framer-motion'
import { useDevice } from 'hooks/useDevice'
import { MediaFormValue } from 'routes/EditSpaceMetadata/schema/spaceMetadata.schema'
import { pillShimmerClass } from 'ui/styles/globals/shimmer.css'

const MAX_THUMB_RETRY_COUNT = 4

export const MediaThumb = (props: {
    media: MediaFormValue
    index: number
    fieldId: string
    isSelected: boolean
    onRemoveItem?: (fieldId: string) => void
    onSelectItem: (fieldId: string) => void
}) => {
    const { media, fieldId, onRemoveItem, onSelectItem, isSelected } = props

    const thumbUrl = media.variants?.find((variant) => variant.name === 'thumbnail')?.url

    const [imageSrc, setImageSrc] = useState<string | undefined>(thumbUrl)
    const [isLoaded, setIsLoaded] = useState(true)
    const [retryCount, setRetryCount] = useState(0)
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    const [isHovering, setIsHovering] = useState(false)
    const pointerDownPositionRef = useRef<{ x: number; y: number } | null>(null)

    useEffect(() => {
        if (retryTimeoutRef.current !== undefined) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = undefined
        }
        setImageSrc(thumbUrl)
        setRetryCount(0)
    }, [thumbUrl])

    useEffect(() => {
        if (!thumbUrl || retryCount === 0 || retryCount > MAX_THUMB_RETRY_COUNT) {
            return
        }

        const delay = SECOND_MS * 2 ** retryCount
        const cacheBusterSeed = `${Date.now()}-${retryCount}`
        const separator = thumbUrl.includes('?') ? '&' : '?'

        retryTimeoutRef.current = setTimeout(() => {
            setImageSrc(`${thumbUrl}${separator}cb=${cacheBusterSeed}`)
            retryTimeoutRef.current = undefined
        }, delay)

        return () => {
            if (retryTimeoutRef.current !== undefined) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = undefined
            }
        }
    }, [retryCount, thumbUrl])

    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current !== undefined) {
                clearTimeout(retryTimeoutRef.current)
                retryTimeoutRef.current = undefined
            }
        }
    }, [])

    const onImageError = useCallback(() => {
        if (!thumbUrl) {
            return
        }

        setIsLoaded(false)

        setRetryCount((prev) => {
            if (prev >= MAX_THUMB_RETRY_COUNT) {
                return prev
            }
            return prev + 1
        })
    }, [thumbUrl])

    const onImageLoad = useCallback(() => {
        if (retryTimeoutRef.current !== undefined) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = undefined
        }
        setRetryCount(0)
        setIsLoaded(true)
    }, [])

    const handlePointerDown = useCallback((event: React.PointerEvent) => {
        pointerDownPositionRef.current = { x: event.clientX, y: event.clientY }
    }, [])

    const handlePointerUp = useCallback(
        (event: React.PointerEvent) => {
            const downPosition = pointerDownPositionRef.current
            if (!downPosition) {
                return
            }

            const deltaX = Math.abs(event.clientX - downPosition.x)
            const deltaY = Math.abs(event.clientY - downPosition.y)

            // If the pointer moved less than 5 pixels, consider it a click
            if (deltaX < 5 && deltaY < 5) {
                onSelectItem(fieldId)
            }

            pointerDownPositionRef.current = null
        },
        [fieldId, onSelectItem],
    )

    const { isTouch } = useDevice()
    const size = isTouch ? 'x7' : 'x10'
    return (
        <BaseThumb
            position="relative"
            background={isLoaded ? 'level1' : undefined}
            className={!isLoaded ? pillShimmerClass : undefined}
            cursor="pointer"
            width="100%"
            height="100%"
            maxWidth={size}
            maxHeight={size}
            minWidth={size}
            minHeight={size}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
        >
            {isSelected && (
                <BaseThumb position="absolute" background="purpleGradient" borderRadius="md" />
            )}

            <Box
                background="level1"
                overflow="hidden"
                zIndex="above"
                userSelect="none"
                pointerEvents="none"
                position="absolute"
                style={{
                    width: isSelected ? 'calc(100% - 4px)' : '100%',
                    height: isSelected ? 'calc(100% - 4px)' : '100%',
                    borderRadius: isSelected ? 15 : undefined,
                    border: isSelected ? '2px solid var(--background)' : undefined,
                }}
            >
                <MotionBox
                    absoluteFill
                    style={{
                        backgroundImage: `url(${imageSrc})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isLoaded ? 1 : 0 }}
                />
            </Box>

            {/* loader */}
            <Box
                position="absolute"
                as="img"
                width="100%"
                height="100%"
                visibility="hidden"
                userSelect="none"
                pointerEvents="none"
                src={imageSrc}
                onLoad={onImageLoad}
                onError={onImageError}
            />

            {/* remove media button */}
            {!!onRemoveItem && (
                <AnimatePresence>
                    {isHovering && (
                        <FadeInBox
                            centerContent
                            position="absolute"
                            top="xs"
                            right="xs"
                            zIndex="above"
                        >
                            <IconButton
                                color="negative"
                                rounded="full"
                                icon="trash"
                                background="level1"
                                opacity="0.8"
                                size="square_xxs"
                                onClick={(event) => {
                                    event.stopPropagation()
                                    onRemoveItem(fieldId)
                                }}
                            />
                        </FadeInBox>
                    )}
                </AnimatePresence>
            )}
        </BaseThumb>
    )
}

export const ProgressThumb = ({ fileName }: { fileName: string }) => {
    return <BaseThumb border background="level2" className={pillShimmerClass} />
}

export const UploadThumb = ({
    onFileDialog,
    displayIcon,
}: {
    onFileDialog: (() => void) | undefined
    displayIcon?: boolean
}) => {
    return (
        <BaseThumb hoverable cursor="pointer" background="level2" onClick={onFileDialog}>
            {displayIcon && (
                <Box
                    centerContent
                    background="level3"
                    rounded="full"
                    width="x3"
                    height="x3"
                    color="cta2"
                >
                    +
                </Box>
            )}
        </BaseThumb>
    )
}

export const PlaceholderThumb = () => {
    return <BaseThumb background="level2" />
}

const BaseThumb = (props: BoxProps) => {
    return (
        <Box
            centerContent
            position="relative"
            rounded="md"
            overflow="hidden"
            userSelect="none"
            width="x10"
            height="x10"
            minHeight="x10"
            minWidth="x10"
            aspectRatio="1/1"
            {...props}
        />
    )
}
